/**
 * Login Action
 * Handles login form submission via React Router v7 actions.
 *
 * Phase 7 (ADR 0003): server-side validation now uses the
 * `loginSchema` zod schema in `src/modules/auth/schemas/`, so the
 * login form and the action share one source of truth. The post-API
 * pipeline (encrypted token storage, Zustand hydration, multi-tab
 * broadcast, success toast, redirect) is unchanged.
 */

import { redirect, type ActionFunctionArgs } from 'react-router-dom';
import { toast } from 'react-toastify';
import { loginUser, storeAuthData } from '@/services/keycloakAuthService';
import { broadcastLogin } from '@/hooks/useLogoutSync';
import { useAuthStore } from '@/stores/authStore';
import { loginSchema } from '@/modules/auth/schemas';
import { getFormDataObject, actionError, transformApiError } from './helpers';

export async function loginAction({ request }: ActionFunctionArgs) {
  try {
    const data = await getFormDataObject(request);

    // Server-side validation via the same zod schema the form uses.
    const parsed = loginSchema.safeParse({
      email: data.email,
      password: data.password,
      remember: data.remember,
    });
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      const errors: Record<string, string> = {};
      // The action surface still maps each field to a single string
      // for backwards-compat with useActionData() consumers; we take
      // the first message per field.
      Object.entries(flat).forEach(([key, msgs]) => {
        if (msgs && msgs.length > 0 && msgs[0]) errors[key] = msgs[0];
      });
      return actionError(errors, 400);
    }

    const { email, password, remember } = parsed.data;

    // Call API
    const response = await loginUser({ email, password });

    if (response.success && response._data) {
      const { _user, credentials, roles, permissions } = response._data;

      const rememberMe = remember === '1';
      await storeAuthData(_user, credentials, roles, permissions, rememberMe);

      useAuthStore.getState().login({
        id: _user.id,
        email: _user.email,
        first_name: _user.first_name || '',
        last_name: _user.last_name || '',
        full_name: `${_user.first_name || ''} ${_user.last_name || ''}`.trim(),
        roles: roles || [],
        permissions: permissions || [],
        phone: _user.phone,
        avatar_url: _user.avatar_url || null,
        organization_name: _user.organization_name,
      });

      broadcastLogin();

      toast.success(`Welcome back, ${_user.first_name || _user.email}!`);

      return redirect('/dashboard');
    }
    return actionError(response.message || 'Login failed', 401);
  } catch (error) {
    console.error('Login action error:', error);

    const errorResponse = transformApiError(error);
    return actionError(errorResponse.errors ?? errorResponse.error ?? 'Login failed', 401);
  }
}
